import { Controller } from '@hotwired/stimulus';
import Choices from 'choices.js';

const LEAVING_PAGE_MESSAGE = {
  en: 'Without saving, your changes will be lost. Do you want to continue?',
  de: 'Ohne zu speichern gehen Ihre Änderungen verloren. Möchten Sie fortfahren?',
};

const RELEVANT_TAGS = 'input, select, textarea, trix-editor';

export default class extends Controller {
  static targets = ['submit'];
  static values = {
    locale: String,
  };

  connect() {
    document.addEventListener('turbo:load', this.initChoices.bind(this));
    this.initValidations();
    this.initUnsavedWarnings();
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.initChoices.bind(this));
    this.destroyChoices();
    this.removeUnsavedWarnings();
  }

  initChoices() {
    if (this.isMobileDevice()) return;

    // Use Choices.js for all <select> elements
    const selectElements = this.element.querySelectorAll('select');
    if (!selectElements.length) {
      console.warn('⚠ No <select> elements found for Choices.js');
      return;
    }

    this.choicesInstances = [];
    Array.from(selectElements).forEach((selectElement) => {
      const choices = new Choices(selectElement, {
        fuseOptions: {
          include: 'score',
          threshold: 0.1,
        },
        shouldSort: false,
        itemSelectText: '',
        allowHTML: true,
      });
      this.choicesInstances.push(choices);
    });
  }

  destroyChoices() {
    if (!this.choicesInstances) return;
    Array.from(this.choicesInstances).forEach((choices) => choices.destroy());
  }

  submit() {
    this.submitTarget.click();
  }

  // Based on https://daverupert.com/2017/11/happier-html5-forms/
  initValidations() {
    const inputs = this.element.querySelectorAll(RELEVANT_TAGS);

    inputs.forEach((input) => {
      this.listenForInvalid(input);
      this.listenforInput(input);
    });
  }

  listenForInvalid(input) {
    input.addEventListener(
      'invalid',
      (event) => {
        if (
          input.type == 'url' &&
          input.value.indexOf('http://') != 0 &&
          input.value.indexOf('https://') != 0
        ) {
          event.preventDefault();
          input.value = `http://${input.value}`;
        } else {
          input.classList.add('has-error');
          input.closest('.form-group').classList.add('has-error');
        }
      },
      false,
    );
  }

  listenforInput(input) {
    input.addEventListener('input', () => {
      let result;
      if (typeof input.checkValidity === 'function')
        result = input.checkValidity();
      else result = true;

      if (result) {
        const formGroup = input.closest('.form-group');
        if (formGroup) {
          formGroup.classList.remove('has-error');

          const formError = formGroup.getElementsByClassName('form-errors')[0];
          if (formError) formError.innerHTML = '';
        }
      }
    });
  }

  isMobileDevice() {
    return (
      'ontouchstart' in document.documentElement || navigator.maxTouchPoints > 0
    );
  }

  // Unsaved changes

  initUnsavedWarnings() {
    this.changed = false;

    const inputs = this.element.querySelectorAll(RELEVANT_TAGS);
    inputs.forEach((input) =>
      input.addEventListener('input', this.formIsChanged.bind(this)),
    );

    window.addEventListener('beforeunload', this.leavingPage.bind(this));
    window.addEventListener('turbo:before-visit', this.leavingPage.bind(this));

    this.submitTarget.addEventListener(
      'click',
      this.allowFormSubmission.bind(this),
    );
  }

  removeUnsavedWarnings() {
    const inputs = this.element.querySelectorAll(RELEVANT_TAGS);
    inputs.forEach((input) => {
      input.removeEventListener('input', this.formIsChanged.bind(this));
    });

    window.removeEventListener('beforeunload', this.leavingPage.bind(this));
    window.removeEventListener(
      'turbo:before-visit',
      this.leavingPage.bind(this),
    );

    this.submitTarget.removeEventListener(
      'click',
      this.allowFormSubmission.bind(this),
    );
  }

  formIsChanged() {
    this.changed = true;
  }

  allowFormSubmission() {
    this.changed = false;
  }

  leavingPage(event) {
    if (this.changed)
      if (event.type == 'turbo:before-visit') {
        if (window.confirm(this.unsavedMessageText)) this.allowFormSubmission();
        else event.preventDefault();
      } else {
        event.returnValue = this.unsavedMessageText;
        return event.returnValue;
      }
  }

  get unsavedMessageText() {
    return LEAVING_PAGE_MESSAGE[this.localeValue.split('-')[0]];
  }
}
